import http from "../../../api/apiClient";
import { createLdsMemoryCache, createLdsRequestCache } from "../../../components/lds/cache";
import { getNoctisOutboxStatus } from "../../../api/noctisOutbox";
import { getNoctisReachability } from "../../../api/noctisReachability";
import { getNoctisStatus } from "../../../api/noctisStatus";
import { getClassroomId, listMyClassrooms } from "../../../services/myEnvironmentService";
import { API_PATH as SCHOOL_CALENDAR_API_PATH } from "../../schoolCalendar/constants";
import { eventScope, normalizeList as normalizeCalendarList } from "../../schoolCalendar/utils";
import type {
  DashboardCounts,
  DashboardQuickSearchResult,
  DashboardUpcomingItem,
  FeedbackFormState,
  FeedbackResponse,
  GiteaIssueSummary,
  GiteaSummary,
  MailchimpStatus,
  NoctisOutboxStatus,
  NoctisReachability,
  NoctisStatus,
} from "../types/dashboard.types";
import { toDateMillis, toDayKey } from "../utils/dashboardDate.utils";
import { getApiStatus, giteaStatusKey } from "../utils/dashboardStatus.utils";
import {
  buildParentMeetingDedupeKey,
  dedupeAndSortUpcoming,
  getDateField,
  getSlotEndValue,
  getSlotStartValue,
  isBookedParentMeeting,
  isFutureOrToday,
  isParentMeetingTitle,
  isPersonalCalendarEvent,
  normalizeList,
  normalizeMeetingTitle,
  readRecord,
  readString,
} from "../utils/dashboardUpcoming.utils";

type CacheKey = "counts" | "personalUpcoming" | "gitea" | "noctis" | "mailchimp";

interface CacheEntry<T> {
  data: T;
  updatedAt: number;
}

const STALE_TIMES: Record<CacheKey, number> = {
  counts: 45_000,
  personalUpcoming: 180_000,
  gitea: 90_000,
  noctis: 90_000,
  mailchimp: 90_000,
};

const countsCache = createLdsMemoryCache<CacheEntry<DashboardCounts>>();
const personalUpcomingCache = createLdsMemoryCache<CacheEntry<DashboardUpcomingItem[]>>();
const giteaCache = createLdsMemoryCache<CacheEntry<GiteaSummary>>();
const noctisCache = createLdsMemoryCache<CacheEntry<{ reachability: NoctisReachability | null; outbox: NoctisOutboxStatus | null; status: NoctisStatus | null }>>();
const mailchimpCache = createLdsMemoryCache<CacheEntry<{ status: MailchimpStatus | null; neverSynced: boolean }>>();

const loadCountsOnce = createLdsRequestCache<DashboardCounts>();
const loadPersonalUpcomingOnce = createLdsRequestCache<DashboardUpcomingItem[]>();
const loadGiteaOnce = createLdsRequestCache<GiteaSummary>();
const loadNoctisOnce = createLdsRequestCache<{ reachability: NoctisReachability | null; outbox: NoctisOutboxStatus | null; status: NoctisStatus | null }>();
const loadMailchimpOnce = createLdsRequestCache<{ status: MailchimpStatus | null; neverSynced: boolean }>();

const isFresh = <T,>(entry: CacheEntry<T> | null, key: CacheKey): entry is CacheEntry<T> =>
  Boolean(entry && Date.now() - entry.updatedAt < STALE_TIMES[key]);

const setCache = <T,>(cache: ReturnType<typeof createLdsMemoryCache<CacheEntry<T>>>, data: T): T => {
  cache.set({ data, updatedAt: Date.now() });
  return data;
};

const optionalString = (value: unknown): string | undefined => {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized || undefined;
};

const requiredString = (value: unknown): string => optionalString(value) ?? "";

export const fetchDashboardQuickSearch = async (
  query: string,
  limit = 8,
  signal?: AbortSignal,
): Promise<DashboardQuickSearchResult[]> => {
  const { data } = await http.get("/dashboard/quick-search", {
    params: { q: query.trim(), limit },
    signal,
  });
  if (!Array.isArray(data)) return [];

  return data.flatMap((entry): DashboardQuickSearchResult[] => {
    const record = readRecord(entry);
    const type = requiredString(record.type).toUpperCase();
    const id = requiredString(record.id);
    const displayName = requiredString(record.displayName);
    if (!id || !displayName) return [];

    if (type === "PARENT") {
      return [{
        type,
        id,
        displayName,
        phone: optionalString(record.phone),
        email: optionalString(record.email),
        childNames: Array.isArray(record.childNames)
          ? record.childNames.map(optionalString).filter((name): name is string => Boolean(name))
          : [],
      }];
    }

    if (type === "CHILD") {
      const parents = Array.isArray(record.parents)
        ? record.parents.flatMap((parent) => {
            const contact = readRecord(parent);
            const parentName = requiredString(contact.displayName);
            if (!parentName) return [];
            return [{
              id: optionalString(contact.id),
              displayName: parentName,
              phone: optionalString(contact.phone),
              email: optionalString(contact.email),
            }];
          })
        : [];
      return [{ type, id, displayName, parents }];
    }

    return [];
  });
};

const dedupeById = (items: Record<string, unknown>[]): Record<string, unknown>[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = item.id ?? item.applicationId;
    if (id == null) return true;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const countApplicationsPending = async (): Promise<number> => {
  try {
    const statuses = ["PENDING", "NEW"];
    const filtered = await Promise.all(statuses.map((status) => http.get(`/applications?status=${status}`)));
    const combinedFiltered = dedupeById(
      filtered.flatMap((res) =>
        normalizeList<Record<string, unknown>>(res.data).filter((application) =>
          statuses.includes(String(application.status || "").toUpperCase()),
        ),
      ),
    );
    if (combinedFiltered.length) return combinedFiltered.length;

    const res = await http.get("/applications");
    return dedupeById(
      normalizeList<Record<string, unknown>>(res.data).filter((application) =>
        statuses.includes(String(application.status || "").toUpperCase()),
      ),
    ).length;
  } catch (error) {
    if (getApiStatus(error) === 403) return 0;
    throw error;
  }
};

const countMutationsPending = async (): Promise<number> => {
  try {
    const res = await http.get("/mutation-requests");
    return normalizeList<Record<string, unknown>>(res.data).filter((request) => String(request.status || "").toUpperCase() === "PENDING").length;
  } catch (error) {
    if (getApiStatus(error) === 403) return 0;
    throw error;
  }
};

const countWaiting = async (): Promise<number> => {
  try {
    const res = await http.get("/children");
    return normalizeList<Record<string, unknown>>(res.data).filter((child) => Boolean(child.waiting)).length;
  } catch (error) {
    if (getApiStatus(error) === 403) return 0;
    throw error;
  }
};

export const getCachedDashboardCounts = (): DashboardCounts | null => countsCache.get()?.data ?? null;
export const getCachedPersonalUpcoming = (): DashboardUpcomingItem[] | null => personalUpcomingCache.get()?.data ?? null;
export const getCachedGiteaSummary = (): GiteaSummary | null => giteaCache.get()?.data ?? null;
export const getCachedNoctisDashboardStatus = () => noctisCache.get()?.data ?? null;
export const getCachedMailchimpDashboardStatus = () => mailchimpCache.get()?.data ?? null;

export const fetchDashboardCounts = async (
  permissions: { canManageApplications: boolean; canManageMutations: boolean; canViewWaitingList: boolean },
  force = false,
): Promise<DashboardCounts> => {
  const cached = countsCache.get();
  if (!force && isFresh(cached, "counts")) return cached.data;

  return loadCountsOnce(async () => {
    const [applications, mutations, waiting] = await Promise.allSettled([
      permissions.canManageApplications ? countApplicationsPending() : Promise.resolve(0),
      permissions.canManageMutations ? countMutationsPending() : Promise.resolve(0),
      permissions.canViewWaitingList ? countWaiting() : Promise.resolve(0),
    ]);
    return setCache(countsCache, {
      applications: applications.status === "fulfilled" ? applications.value : 0,
      mutations: mutations.status === "fulfilled" ? mutations.value : 0,
      waiting: waiting.status === "fulfilled" ? waiting.value : 0,
    });
  });
};

export const fetchGiteaDashboardStatus = async (force = false): Promise<GiteaSummary> => {
  const cached = giteaCache.get();
  if (!force && isFresh(cached, "gitea")) return cached.data;

  return loadGiteaOnce(async () => {
    const res = await http.get("/admin/gitea/issues/recent", { params: { limit: 50 } });
    const configured = Boolean(readRecord(res.data).configured);
    const issues = Array.isArray(readRecord(res.data).issues) ? (readRecord(res.data).issues as GiteaIssueSummary[]) : [];
    const counts: GiteaSummary["counts"] = {};
    issues.forEach((issue) => {
      const key = giteaStatusKey(issue.workflowStatus ?? issue.workflow_status ?? issue.state ?? "");
      if (key === "unknown") return;
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return setCache(giteaCache, { configured, total: issues.length, counts });
  });
};

export const fetchNoctisDashboardStatus = async (force = false) => {
  const cached = noctisCache.get();
  if (!force && isFresh(cached, "noctis")) return cached.data;

  return loadNoctisOnce(async () => {
    const [reachability, outbox, status] = await Promise.all([
      getNoctisReachability() as Promise<NoctisReachability>,
      getNoctisOutboxStatus() as Promise<NoctisOutboxStatus>,
      getNoctisStatus() as Promise<NoctisStatus>,
    ]);
    return setCache(noctisCache, {
      reachability: reachability ?? null,
      outbox: outbox ?? null,
      status: status ?? null,
    });
  });
};

export const fetchMailchimpDashboardStatus = async (force = false): Promise<{ status: MailchimpStatus | null; neverSynced: boolean }> => {
  const cached = mailchimpCache.get();
  if (!force && isFresh(cached, "mailchimp")) return cached.data;

  return loadMailchimpOnce(async () => {
    const res = await http.get("/admin/newsletter/recipients/mailchimp/status", {
      validateStatus: (code: number) => (code >= 200 && code < 300) || code === 204,
    });
    if (res.status === 204) return setCache(mailchimpCache, { status: null, neverSynced: true });
    return setCache(mailchimpCache, { status: readRecord(res.data) as MailchimpStatus, neverSynced: false });
  });
};

export const fetchPersonalUpcoming = async (
  permissions: {
    canSeeMyEnvironment: boolean;
    canLoadPersonalCalendar: boolean;
    canViewMyParentMeetings: boolean;
  },
  labels: {
    schoolCalendar: string;
    agendaActivities: string;
    parentMeetings: string;
    parentMeetingWithChild: (child: string) => string;
    untitled: string;
  },
  force = false,
): Promise<DashboardUpcomingItem[]> => {
  if (!permissions.canSeeMyEnvironment) return [];
  const cached = personalUpcomingCache.get();
  if (!force && isFresh(cached, "personalUpcoming")) return cached.data;

  return loadPersonalUpcomingOnce(async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming: DashboardUpcomingItem[] = [];

    if (permissions.canLoadPersonalCalendar) {
      try {
        const classrooms = await listMyClassrooms();
        const classroomIds = new Set(classrooms.map(getClassroomId).filter(Boolean).map(String));
        const { data } = await http.get(SCHOOL_CALENDAR_API_PATH);

        normalizeCalendarList<unknown>(data)
          .filter((event) => isPersonalCalendarEvent(event, classroomIds))
          .forEach((event) => {
            const record = readRecord(event);
            const startValue = getDateField(record, ["startDate", "startsAt", "start"]);
            const endValue = getDateField(record, ["endDate", "endsAt", "end"]);
            const time = toDateMillis(startValue);
            if (!Number.isFinite(time) || time < today.getTime()) return;
            const title = readString(record, ["title"], labels.untitled);
            const scope = eventScope(event);
            const isParentMeetingCalendarItem = isParentMeetingTitle(title);
            const type = scope === "SCHOOL" ? labels.schoolCalendar : labels.agendaActivities;
            const parentMeetingDedupeKey = isParentMeetingCalendarItem ? buildParentMeetingDedupeKey({ title, start: startValue }) : null;
            const eventId = record.uuid ?? record.id ?? record.eventId ?? `${title}-${time}`;

            upcoming.push({
              id: `calendar-${String(eventId)}`,
              dedupeKey: isParentMeetingCalendarItem
                ? String(parentMeetingDedupeKey)
                : `calendar-${String(eventId ?? `${scope}-${toDayKey(time)}-${normalizeMeetingTitle(title)}`)}`,
              parentMeetingDedupeKey,
              source: "calendar",
              title,
              date: startValue,
              endDate: endValue,
              allDay: Boolean(record.allDay ?? record.all_day),
              type,
              time,
              details: {
                title,
                description: readString(record, ["description"]),
                location: readString(record, ["location"]),
                scope,
                eventType: readString(record, ["eventType", "type"]),
                raw: event,
              },
              path: "/my/outings",
            });
          });
      } catch {
        // Calendar failures are local to the personal upcoming panel.
      }
    }

    if (permissions.canViewMyParentMeetings) {
      try {
        const { data } = await http.get("/parent-meetings/overview");
        normalizeList<unknown>(data).forEach((plan) => {
          const planRecord = readRecord(plan);
          const meetings = Array.isArray(planRecord.plannedMeetings) ? planRecord.plannedMeetings : [];
          meetings.forEach((meeting) => {
            if (!isBookedParentMeeting(meeting)) return;
            const record = readRecord(meeting);
            const startValue = getSlotStartValue(meeting);
            const endValue = getSlotEndValue(meeting);
            const time = toDateMillis(startValue);
            const endTime = toDateMillis(endValue);
            if (!isFutureOrToday(startValue, today) || !Number.isFinite(endTime) || endTime <= time) return;
            const childName = readString(record, ["childName", "childFullName", "studentName", "pupilName"]);
            const meetingId = record.id ?? record.uuid ?? record.meetingId ?? record.slotId ?? record.selectedSlotId ?? "";
            const planId = planRecord.id ?? planRecord.planId ?? "plan";
            const title = childName ? labels.parentMeetingWithChild(childName) : readString(planRecord, ["title"], labels.parentMeetings);
            const parentMeetingDedupeKey = buildParentMeetingDedupeKey({ title, childName, start: startValue });

            upcoming.push({
              id: `meeting-${String(meetingId || `${String(planId)}-${time}-${childName}`)}`,
              dedupeKey: parentMeetingDedupeKey,
              parentMeetingDedupeKey,
              source: "parentMeeting",
              title,
              date: startValue,
              endDate: endValue,
              allDay: false,
              type: labels.parentMeetings,
              time,
              details: {
                childName,
                classroomName: readString(planRecord, ["classroomName"]),
                planTitle: readString(planRecord, ["title"]),
                parentName: readString(record, ["parentName"]),
                parentEmail: readString(record, ["parentEmailConfirmed", "parentEmailOriginal"]),
                teacherName: readString(planRecord, ["teacherName"]),
                teacherEmail: readString(planRecord, ["teacherEmail"]),
                status: readString(record, ["status"], readString(planRecord, ["status"])),
                raw: meeting,
              },
              path: "/my/parent-meetings",
            });
          });
        });
      } catch {
        // Parent meeting failures are local to the personal upcoming panel.
      }
    }

    return setCache(personalUpcomingCache, dedupeAndSortUpcoming(upcoming));
  });
};

export const submitDashboardFeedback = async (
  form: FeedbackFormState,
  requesterName: string,
): Promise<FeedbackResponse> => {
  const payload = {
    title: form.title.trim() || "(no title)",
    description: form.body.trim(),
    pageUrl: window.location.href,
    userAgent: window.navigator.userAgent,
    requesterName: requesterName || undefined,
    screenshotsBase64: form.screenshots.length ? form.screenshots.map((screenshot) => screenshot.base64) : undefined,
  };
  const { data } = await http.post("/issues", payload);
  return readRecord(data) as FeedbackResponse;
};

export const dashboardStaleTimes = STALE_TIMES;
